import { useCallback, useState } from 'react';

/**
 * Expanded-state for an Accordion of cards, persisted to sessionStorage.
 *
 * Session-scoped: expansion survives a refresh or a trip to a detail page and back, but a new
 * tab starts collapsed. Each list passes its own `storageKey` so pages don't share expansion.
 */

const readPersistedIds = (storageKey: string): Array<string> => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    return [];
  }
};

const persistIds = (storageKey: string, ids: Array<string>): void => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(ids));
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
};

// Accordion.Root reports a bare string for single-expand and an array for multi-expand.
const toIdList = (value: string | Array<string>): Array<string> =>
  Array.isArray(value) ? value : value ? [value] : [];

export const useExpandedGroups = (storageKey: string) => {
  // Keyed by storageKey so a caller that swaps keys re-reads that key's ids instead of
  // carrying the previous list over and persisting it under the new key.
  const [state, setState] = useState<{ storageKey: string; ids: Array<string> }>(() => ({
    storageKey,
    ids: readPersistedIds(storageKey),
  }));

  const expandedIds = state.storageKey === storageKey ? state.ids : readPersistedIds(storageKey);

  const onExpandedChange = useCallback(
    (value: string | Array<string>) => {
      const ids = toIdList(value);
      setState({ storageKey, ids });
      persistIds(storageKey, ids);
    },
    [storageKey],
  );

  const isExpanded = useCallback((id: string) => expandedIds.includes(id), [expandedIds]);

  return { expandedIds, isExpanded, onExpandedChange };
};
